// @owners { team: patients-team }
import { SPACING } from '@alto/design-library-tokens';
import {
  Body,
  Button,
  Column,
  H1,
  Illustration,
  InputCheckbox,
  LgPadding,
  LgSpacing,
  MdPadding,
  Row,
  Toast,
  ToastContext,
  useScreenSize,
} from '@alto/design-system';
// eslint-disable-next-line @alto/no-pocky-import
import { Modal, ModalContent } from '@alto/pocky';
import React, { useContext, useEffect } from 'react';
import { useController, useForm } from 'react-hook-form';
// eslint-disable-next-line import/no-deprecated
import { updateUser } from '~shared/actions/users';
import { DEFAULT_ERROR_ALERT_MESSAGE, DEFAULT_ERROR_ALERT_TITLE, IMG_ALT_TEXTS } from '~shared/constants';
import getUserPhiAuth from '~shared/features/users/selectors/getUserPhiAuth';
import { type UserPhiAuthValues } from '~shared/helpers/communicationPreferences';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import messagingImage from '~web/images/messaging@3x.png';

export type Props = {
  readonly onClose: () => void;
};

export const PhiAuthModal = ({ onClose }: Props) => {
  const dispatch = useDispatchShared();
  const { addToast } = useContext(ToastContext);
  const { trackEvent } = useAnalytics();
  const userPhiAuth = useSelectorShared(getUserPhiAuth);
  const { isSMScreenOrBigger } = useScreenSize();

  useEffect(() => {
    trackEvent({
      event: EVENTS.PHI_PREFERENCES_INTERSTITIAL_VIEWED,
    });
  }, [trackEvent]);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      sms_authorized: userPhiAuth?.sms_authorized || false,
      push_authorized: userPhiAuth?.push_authorized || false,
      email_authorized: userPhiAuth?.email_authorized || false,
    },
  });

  const smsAuthController = useController({
    control,
    name: 'sms_authorized',
    rules: { required: false },
  });
  const emailAuthController = useController({
    control,
    name: 'email_authorized',
    rules: { required: false },
  });
  const pushAuthController = useController({
    control,
    name: 'push_authorized',
    rules: { required: false },
  });

  const onSubmit = async ({ sms_authorized, email_authorized, push_authorized }: UserPhiAuthValues) => {
    const phi_auth_data = {
      sms_authorized,
      email_authorized,
      push_authorized,
    };
    const { success } = await dispatch(
      // eslint-disable-next-line import/no-deprecated
      updateUser({
        phi_auth_data: JSON.stringify(phi_auth_data),
      }),
    );

    if (success) {
      addToast(<Toast>Preferences saved! To make changes, visit Account.</Toast>);
      trackEvent({ event: EVENTS.PHI_PREFERENCES_INTERSTITIAL_SUBMITTED });
    } else {
      addToast(
        <Toast variant="error">
          {DEFAULT_ERROR_ALERT_TITLE}. {DEFAULT_ERROR_ALERT_MESSAGE}
        </Toast>,
      );
    }
    onClose();
  };

  const handleClose = async () => {
    const phi_auth_data = {
      sms_authorized: null,
      email_authorized: null,
      push_authorized: null,
    };
    const { success } = await dispatch(
      // eslint-disable-next-line import/no-deprecated
      updateUser({
        phi_auth_data: JSON.stringify(phi_auth_data),
      }),
    );

    if (success) {
      addToast(<Toast>Preferences saved! To make changes, visit Account.</Toast>);
      trackEvent({ event: EVENTS.PHI_PREFERENCES_INTERSTITIAL_DISMISSED });
    } else {
      addToast(
        <Toast variant="error">
          {DEFAULT_ERROR_ALERT_TITLE}. {DEFAULT_ERROR_ALERT_MESSAGE}
        </Toast>,
      );
    }
    onClose();
  };

  return (
    <Modal
      showDismiss
      dismissOnOutsideClick
      showing
      onClose={handleClose}
    >
      <ModalContent>
        {isSMScreenOrBigger ? (
          <>
            <Row center>
              <Illustration
                size="xl"
                objectFit="contain"
                resizeMode="contain"
                source={messagingImage}
                accessibilityLabel={IMG_ALT_TEXTS.handheldMessaging}
              />
            </Row>
            <LgSpacing />
          </>
        ) : null}
        <H1 center>Health information preferences</H1>
        <Column>
          <LgPadding bottomPadding={SPACING.STATIC.NONE}>
            <Body>
              We message you medication and order updates. Can we include health information (like medication names) in
              push notifications, text messages, and emails?
            </Body>
          </LgPadding>
        </Column>
        <LgSpacing />
        <Column gap="XS">
          <InputCheckbox
            name="push_authorized"
            label="Push notifications"
            onPress={() => {
              pushAuthController.field.onChange(!pushAuthController.field.value);
            }}
            value={pushAuthController.field.value}
          />
          <InputCheckbox
            name="sms_authorized"
            label="Text messages/SMS"
            onPress={() => {
              smsAuthController.field.onChange(!smsAuthController.field.value);
            }}
            value={smsAuthController.field.value}
          />
          <InputCheckbox
            name="email_authorized"
            label="Emails"
            onPress={() => {
              emailAuthController.field.onChange(!emailAuthController.field.value);
            }}
            value={emailAuthController.field.value}
          />
          <Row center>
            <MdPadding>
              <Button
                label="Submit"
                accessibilityLabel="Press to submit contact preferences"
                onPress={handleSubmit(onSubmit)}
                loading={isSubmitting}
                loadingLabel="Submitting"
              />
            </MdPadding>
          </Row>
        </Column>
      </ModalContent>
    </Modal>
  );
};
